import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Settings2 } from "lucide-react";
import { api, errorMessage, fieldErrors, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABELS } from "@/lib/perms";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Input } from "@/pages/admin/ManagePages";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";

export const ROLE_OPTIONS = [["system_admin", ROLE_LABELS.system_admin], ["super_admin", ROLE_LABELS.super_admin], ["admin_sede", ROLE_LABELS.admin_sede]];
export const statusLabel = (r) => (r.invitation_pending ? "Invito in attesa" : r.active ? "Attivo" : "Disattivato");
const STATUS = ["Attivo", "Disattivato", "Invito in attesa"];
const EMPTY = { first_name: "", last_name: "", email: "", role: "admin_sede", spin_id: "", mode: "invite", temp_password: "" };

const CreateForm = ({ system, spins, onDone, onCancel }) => {
    const [form, setForm] = useState({ ...EMPTY });
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState("");
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        const { mode, temp_password: tp, ...body } = form;
        if (!system) body.role = "admin_sede";
        try { const { data } = await api.post("/admin/users", mode === "temp" ? { ...body, temp_password: tp } : body); onDone(data.message); }
        catch (err) { setErrors(fieldErrors(err) || {}); setMsg(fieldErrors(err) ? "" : errorMessage(err)); }
    };
    return (
        <form noValidate onSubmit={submit} className="edi-card p-5 mb-6 grid gap-4 sm:grid-cols-2" data-testid="admin-form">
            <h2 className="sm:col-span-2 text-xl font-bold">Nuovo account</h2>
            <Input id="adm-first_name" label="Nome" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} error={errors.first_name} />
            <Input id="adm-last_name" label="Cognome" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} error={errors.last_name} />
            <Input id="adm-email" label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} error={errors.email} />
            {system && (
                <Input as="select" id="adm-role" label="Ruolo" value={form.role} onChange={(e) => set("role", e.target.value)} error={errors.role}>
                    {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Input>
            )}
            {form.role === "admin_sede" && (
                <Input as="select" id="adm-spin" label="Sede" value={form.spin_id} onChange={(e) => set("spin_id", e.target.value)} error={errors.spin_id}>
                    <option value="">Seleziona…</option>{spins.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Input>
            )}
            {system && (
                <fieldset className="sm:col-span-2">
                    <legend className="edi-label">Attivazione</legend>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                        <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="radio" className="h-5 w-5" name="adm-mode" checked={form.mode === "invite"} onChange={() => set("mode", "invite")} data-testid="adm-mode-invite" /> Invia invito via email</label>
                        <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="radio" className="h-5 w-5" name="adm-mode" checked={form.mode === "temp"} onChange={() => set("mode", "temp")} data-testid="adm-mode-temp" /> Imposta password temporanea</label>
                    </div>
                </fieldset>
            )}
            {system && form.mode === "temp" && (
                <Input id="adm-temp_password" type="password" label="Password temporanea" autoComplete="new-password" value={form.temp_password} onChange={(e) => set("temp_password", e.target.value)} error={errors.temp_password} />
            )}
            <div className="sm:col-span-2 flex flex-wrap gap-3">
                <button type="submit" className="btn-primary" data-testid="admin-save-button">{form.mode === "temp" ? "Crea account" : "Crea e invia invito"}</button>
                <button type="button" className="btn-secondary" onClick={onCancel} data-testid="admin-cancel-button">Annulla</button>
            </div>
            {msg && <div className="sm:col-span-2"><Notice kind="error" role="alert" testId="admin-form-error">{msg}</Notice></div>}
        </form>
    );
};

const Filters = ({ f, setF, spins, system }) => (
    <div className="edi-card p-4 mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end" role="search" aria-label="Cerca e filtra gli account" data-testid="users-filters">
        <div>
            <label htmlFor="users-q" className="edi-label">Cerca</label>
            <div className="relative">
                <Search aria-hidden="true" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-edi-muted" />
                <input id="users-q" type="search" className="edi-input pl-10" placeholder="Nome o email" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="users-search" />
            </div>
        </div>
        {system && (
            <div>
                <label htmlFor="users-role" className="edi-label">Ruolo</label>
                <select id="users-role" className="edi-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="users-filter-role">
                    <option value="">Tutti i ruoli</option>{ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </div>
        )}
        <div>
            <label htmlFor="users-spin" className="edi-label">Sede</label>
            <select id="users-spin" className="edi-input" value={f.spin} onChange={(e) => setF({ ...f, spin: e.target.value })} data-testid="users-filter-spin">
                <option value="">Tutte le sedi</option>{spins.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
        </div>
        <div>
            <label htmlFor="users-status" className="edi-label">Stato</label>
            <select id="users-status" className="edi-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="users-filter-status">
                <option value="">Tutti gli stati</option>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
    </div>
);

const name = (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email;

const UsersList = ({ rows, onOpen }) => (
    <div className="edi-card" data-testid="admins-table">
        <h2 className="font-heading font-bold text-base px-4 pt-4 pb-2" id="users-caption">Account ({rows.length})</h2>
        {rows.length === 0 && <p className="px-4 pb-4" data-testid="admins-table-empty">Nessun account corrisponde ai filtri</p>}
        <table className="hidden md:table w-full text-left text-sm" aria-labelledby="users-caption">
            <thead><tr className="border-b-2 border-edi-border-strong">
                {["Nome", "Email", "Ruolo", "Sede", "Stato", "Ultimo accesso"].map((h) => <th key={h} scope="col" className="px-4 py-2">{h}</th>)}
                <th scope="col" className="px-4 py-2"><span className="sr-only">Azioni</span></th>
            </tr></thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.id} className="border-b border-edi-border" data-testid={`admins-row-${r.id}`}>
                        <td className="px-4 py-3 font-semibold">{name(r)}</td>
                        <td className="px-4 py-3 [overflow-wrap:anywhere]">{r.email}</td>
                        <td className="px-4 py-3">{ROLE_LABELS[r.role]}</td>
                        <td className="px-4 py-3">{r.role === "admin_sede" ? r.spin_name : "Tutte"}</td>
                        <td className="px-4 py-3">{statusLabel(r)}</td>
                        <td className="px-4 py-3">{formatDate(r.last_login_at) || "—"}</td>
                        <td className="px-4 py-3 text-right">
                            <button type="button" className="btn-secondary py-2 px-4" onClick={() => onOpen(r)} aria-label={`Gestisci ${name(r)}`} data-testid={`admin-manage-${r.id}`}>
                                <Settings2 aria-hidden="true" size={16} /> Gestisci
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <ul className="md:hidden divide-y divide-edi-border" aria-labelledby="users-caption">
            {rows.map((r) => (
                <li key={r.id} className="p-4" data-testid={`admins-card-${r.id}`}>
                    <p className="font-semibold">{name(r)}</p>
                    <p className="text-sm [overflow-wrap:anywhere]">{r.email}</p>
                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                        <dt className="text-edi-muted">Ruolo</dt><dd>{ROLE_LABELS[r.role]}</dd>
                        <dt className="text-edi-muted">Sede</dt><dd>{r.role === "admin_sede" ? r.spin_name : "Tutte"}</dd>
                        <dt className="text-edi-muted">Stato</dt><dd>{statusLabel(r)}</dd>
                        <dt className="text-edi-muted">Ultimo accesso</dt><dd>{formatDate(r.last_login_at) || "—"}</dd>
                    </dl>
                    <button type="button" className="btn-secondary py-2 px-4 mt-3 w-full" onClick={() => onOpen(r)} aria-label={`Gestisci ${name(r)}`} data-testid={`admin-manage-card-${r.id}`}>
                        <Settings2 aria-hidden="true" size={16} /> Gestisci
                    </button>
                </li>
            ))}
        </ul>
    </div>
);

export const AdminAccounts = ({ system }) => {
    const title = system ? "Utenti e permessi" : "Amministratori di sede";
    usePageTitle(title);
    const { user } = useAuth();
    const navigate = useNavigate();
    const edit = system || can(user, "amministratori", "M");
    const [items, setItems] = useState(null);
    const [spins, setSpins] = useState([]);
    const [creating, setCreating] = useState(false);
    const [msg, setMsg] = useState("");
    const [f, setF] = useState({ q: "", role: "", spin: "", status: "" });
    const load = () => api.get("/admin/users").then(({ data }) => setItems(system ? data.items : data.items.filter((u) => u.role === "admin_sede")));
    useEffect(() => { load(); api.get("/admin/filter-options").then(({ data }) => setSpins(data.spin || [])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const rows = useMemo(() => (items || []).filter((r) => {
        const q = f.q.trim().toLowerCase();
        return (!q || `${name(r)} ${r.email} ${r.spin_name || ""}`.toLowerCase().includes(q)) && (!f.role || r.role === f.role)
            && (!f.spin || r.spin_id === f.spin) && (!f.status || statusLabel(r) === f.status);
    }), [items, f]);
    if (!items) return <Loading />;
    const base = system ? "/amministrazione/utenti" : "/amministrazione/amministratori";
    return (
        <>
            <PageHead title={title}>
                {edit && <button type="button" className="btn-primary py-2" onClick={() => setCreating(true)} data-testid="admin-create-button">Nuovo account</button>}
            </PageHead>
            {msg && <div className="mb-4"><Notice kind="success" testId="admins-message">{msg}</Notice></div>}
            {creating && <CreateForm system={system} spins={spins} onCancel={() => setCreating(false)} onDone={(m) => { setCreating(false); setMsg(m); load(); }} />}
            <Filters f={f} setF={setF} spins={spins} system={system} />
            <p className="sr-only" role="status" aria-live="polite">{`${rows.length} account trovati`}</p>
            <UsersList rows={rows} onOpen={(r) => navigate(`${base}/${r.id}`)} />
        </>
    );
};

export const UtentiPermessi = () => <AdminAccounts system />;
export const Amministratori = () => <AdminAccounts />;
